/**
 * Zentrale Quelle für Item-Qualitäts- und Varianten-Badges (REIN/GESTRECKT).
 * Ersetzt vormals vier parallele, teils unvollständige Implementierungen
 * (crimeOps.ts, rolltisch-tab.ts, gewaechshaus-tab.ts, streetmarket-tab.ts).
 * Qualität/Variante gehören ausschließlich ins Badge, niemals in den Item-Namen.
 */

export interface ItemBadge {
  label: string;
  colorVar: string;
  /** Optionale zusätzliche CSS-Klasse (z.B. Shimmer-Animation), global in dashboard.css definiert. */
  extraClass?: string;
}

export const QUALITY_BADGE_META: Record<number, ItemBadge> = {
  1: { label: 'AUSSCHUSS', colorVar: '--text-muted' },
  2: { label: 'HINTERHOF-WARE', colorVar: '--accent-red' },
  3: { label: 'MARKTWARE', colorVar: '--accent-orange' },
  4: { label: 'FEINWARE', colorVar: '--accent-green' },
  5: { label: 'PREMIUM-ERNTE', colorVar: '--accent-gold' },
};

export const VARIANT_BADGE_REIN: ItemBadge = { label: 'REIN', colorVar: '--accent-purple' };
export const VARIANT_BADGE_GESTRECKT: ItemBadge = { label: 'GESTRECKT', colorVar: '--accent-blue' };

/**
 * Reinheits-Badge für die Pulver-Linie unterhalb der Prismakristall/Reinware-Schwelle
 * (NT-QUALITY-LAB-01). laborpulver/reinsubstanz erreichen deterministisch IMMER mindestens
 * PRISMAKRISTALL (siehe getLabPurityBadge) — nur strassenpulver bleibt darunter.
 */
export const PULVER_BADGE_META: Record<string, ItemBadge> = {
  strassenpulver: { label: 'STRASSENWARE', colorVar: '--accent-red' },
};

/** Gibt das Reinheits-Badge für eine Pulver-Sorte zurück (Fallback unterhalb jeder Schwelle). */
export function getPulverBadge(pulverSorte: string): ItemBadge | null {
  return PULVER_BADGE_META[pulverSorte] ?? null;
}

/**
 * Reinheits-Modifier + Kristallin-Produkt-Cap (NT-QUALITY-LAB-01) — dupliziert aus
 * apps/backend/src/modules/labor/labor.definitions.ts (REINHEITS_MODIFIER /
 * KRISTALLIN_MODIFIER_CAP), reine Anzeige-Werte ohne Shared-Import (analog
 * BUG_REPORT_DISCORD_TAGS/EU-Länder-Pattern). Bei Änderung dort auch hier nachziehen.
 */
const REINHEITS_MODIFIER: Readonly<Record<string, number>> = {
  strassenpulver: 0.7,
  laborpulver:    1.0,
  reinsubstanz:   1.4,
};
const KRISTALLIN_MODIFIER_CAP: Readonly<Record<string, number>> = {
  axion:    1.0,
  ax_0626:  1.0,
  parallax: 1.4,
};
const LAB_REINSUBSTANZ_PRISMAKRISTALL_MIN = 1.0;
const LAB_REINSUBSTANZ_REINWARE_MIN       = 1.4;

/**
 * Badges für die beiden höchsten Reinheits-Stufen (NT-QUALITY-LAB-01).
 * PRISMAKRISTALL schimmert (`.badge-prismakristall`, dashboard.css) — REINWARE bleibt
 * statisch, damit die beiden Stufen weiterhin farblich unterscheidbar sind.
 */
export const PURITY_BADGE_META: Record<'PRISMAKRISTALL' | 'REINWARE', ItemBadge> = {
  PRISMAKRISTALL: { label: 'PRISMAKRISTALL', colorVar: '--accent-blue', extraClass: 'badge-prismakristall' },
  REINWARE:       { label: 'REINWARE',       colorVar: '--accent-gold' },
};

/**
 * Bestimmt das Reinheits-Badge deterministisch aus Pulversorte + optionalem Kristallin-Produkt
 * (Produkt-Cap greift nur bei Kristallin-Outputs, nicht bei der Pulver-Linie direkt).
 * Gibt null zurück, wenn die Reinheit unter der Prismakristall-Schwelle liegt.
 */
export function getLabPurityBadge(pulverSorte: string, kristallinProdukt?: string): ItemBadge | null {
  const base = REINHEITS_MODIFIER[pulverSorte];
  if (base === undefined) return null;
  const cap = kristallinProdukt !== undefined ? KRISTALLIN_MODIFIER_CAP[kristallinProdukt] : undefined;
  const effective = cap !== undefined ? Math.min(base, cap) : base;
  if (effective >= LAB_REINSUBSTANZ_REINWARE_MIN) return PURITY_BADGE_META.REINWARE;
  if (effective >= LAB_REINSUBSTANZ_PRISMAKRISTALL_MIN) return PURITY_BADGE_META.PRISMAKRISTALL;
  return null;
}

/** Kristallin-Produkt-Namen (für getCleanItemName). */
const KRISTALLIN_LABELS: Record<string, string> = {
  axion:    'Axion',
  ax_0626:  'AX-0626',
  parallax: 'Parallax',
};

/** Labor-Recipe-Key → Anzeigename des Output-Items (ohne "output_"-Prefix). */
const LABOR_OUTPUT_LABELS: Record<string, string> = {
  schnaps:             'Hausgemachter Schnaps',
  bier:                'Bier',
  nahrungsergaenzung:  'Gefälschte Nahrungsergänzung',
  parfuem:             'Parfüm',
  energydrink:         'Experimentelle Energydrinks',
  schnaps_illegal:     'Hausgemachter Schnaps (illegal)',
  parfuem_illegal:     'Designerparfüm-Fake',
  energydrink_illegal: 'Experimentelle Energydrinks (illegal)',
  pharma:              'Pharmazeutische Extras',
  blaues_zeug:         'Das blaue Zeug',
  rotes_zeug:          'Das rote Zeug',
  synthese_x:          'Syntheseprodukt X',
};

export function extractQualityTier(itemKey: string): number | null {
  const m = /_q([1-5])$/.exec(itemKey);
  return m ? Number(m[1]) : null;
}

export function getQualityBadge(itemKey: string): ItemBadge | null {
  const tier = extractQualityTier(itemKey);
  return tier !== null ? (QUALITY_BADGE_META[tier] ?? null) : null;
}

/** joint_pur → REIN, joint_mix → GESTRECKT. blunt hat kein Pur/Mix-Konzept (Tabak ist Pflicht-Hauptzutat). */
export function getVariantBadge(itemKey: string): ItemBadge | null {
  if (itemKey.startsWith('joint_pur_')) return VARIANT_BADGE_REIN;
  if (itemKey.startsWith('joint_mix_')) return VARIANT_BADGE_GESTRECKT;
  return null;
}

function titleCase(s: string): string {
  return s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Sorten-Keys bleiben ASCII (`oe`/`ae`/`ue`), tragen aber echte Umlaute im Label
 * (NT-I18N-UMLAUTE-01) — dupliziert aus garden.definitions.ts (HANF_STRAINS), da
 * generisches titleCase() Umlaute nicht kennt. Bei neuen Sorten mit Umlaut hier nachziehen.
 */
const STRAIN_LABEL_OVERRIDES: Readonly<Record<string, string>> = {
  kraeuter_mix:  'Kräuter-Mix',
  balkonkoenig:  'Balkonkönig',
  gruener_riese: 'Grüner Riese',
  weisser_nebel: 'Weißer Nebel',
};

/** Wie titleCase(), nutzt aber echte Umlaut-Labels für bekannte Sorten-Keys. */
function strainLabel(key: string): string {
  return STRAIN_LABEL_OVERRIDES[key] ?? titleCase(key);
}

/**
 * Liefert den reinen Item-Anzeigenamen ohne Qualitätsstufe und ohne Pur/Mix-Text.
 * Bei Zwei-Zutaten-Items (Joint Mix, Blunt) bleiben beide Zutat-Namen erhalten,
 * da sie zur Unterscheidung verschiedener Stacks nötig sind.
 */
export function getCleanItemName(itemKey: string): string {
  let m: RegExpExecArray | null;
  if ((m = /^getrockneter_tabak_([a-z0-9_]+)_q[1-5]$/.exec(itemKey))) return `${strainLabel(m[1] ?? '')} (getrocknet)`;
  if ((m = /^tabakfeinschnitt_([a-z0-9_]+)_q[1-5]$/.exec(itemKey))) return `Feinschnitt ${strainLabel(m[1] ?? '')}`;
  if ((m = /^deckblatt_getrocknet_q[1-5]$/.exec(itemKey))) return 'Deckblatt';
  if ((m = /^zigarettenschachtel_([a-z0-9_]+)_q[1-5]$/.exec(itemKey))) return `Zigarettenschachtel ${strainLabel(m[1] ?? '')}`;
  if ((m = /^zigarrenkiste_([a-z0-9_]+)_q[1-5]$/.exec(itemKey))) return `Zigarrenkiste ${strainLabel(m[1] ?? '')}`;
  if ((m = /^joint_pur_([a-z0-9_]+)_q[1-5]$/.exec(itemKey))) return `Joint ${strainLabel(m[1] ?? '')}`;
  if ((m = /^joint_mix_([a-z0-9_]+)__([a-z0-9_]+)_q[1-5]$/.exec(itemKey))) return `Joint ${strainLabel(m[1] ?? '')} + ${strainLabel(m[2] ?? '')}`;
  if ((m = /^blunt_([a-z0-9_]+)__([a-z0-9_]+)_q[1-5]$/.exec(itemKey))) return `Blunt ${strainLabel(m[1] ?? '')} + ${strainLabel(m[2] ?? '')}`;
  // Labor-Output mit Reinheits-Modifier: output_X__pulversorte (Kristallin)
  if ((m = /^output_([a-z0-9_]+)__([a-z_]+)$/.exec(itemKey))) {
    return KRISTALLIN_LABELS[m[1] ?? ''] ?? LABOR_OUTPUT_LABELS[m[1] ?? ''] ?? titleCase(m[1] ?? '');
  }
  // Einfacher Labor-Output: output_schnaps, output_blaues_zeug usw.
  if ((m = /^output_([a-z0-9_]+)$/.exec(itemKey))) {
    return LABOR_OUTPUT_LABELS[m[1] ?? ''] ?? KRISTALLIN_LABELS[m[1] ?? ''] ?? titleCase(m[1] ?? '');
  }
  if (itemKey === 'zigarettenhuelsen') return 'Zigarettenhülsen';
  if (itemKey === 'blaettchen') return 'Blättchen';
  // Generischer Fallback (z. B. rohe Cannabis-Ernte "stadtgras_q5"): Qualitätssuffix entfernen, falls vorhanden.
  return strainLabel(itemKey.replace(/_q[1-5]$/, ''));
}

function esc(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

/** Badge-Markup: Border + Textfarbe, kein Fill, UPPERCASE-Label — gleicher Stil projektweit. */
export function badgeHtml(badge: ItemBadge, cssClass: string): string {
  const classes = badge.extraClass ? `${cssClass} ${badge.extraClass}` : cssClass;
  return `<span class="${classes}" style="color:var(${badge.colorVar});border-color:var(${badge.colorVar});">${esc(badge.label)}</span>`;
}

/**
 * Gibt das Reinheits-Badge für output_X__pulversorte-Keys zurück (Kristallin-Output,
 * Produkt-Cap greift über X). Wird auch für reine Pulver-Items (strassenpulver,
 * laborpulver, reinsubstanz) genutzt — dort ohne Produkt-Cap.
 * Prüft zuerst die Prismakristall/Reinware-Schwelle (NT-QUALITY-LAB-01), sonst Fallback
 * auf das Basis-Pulver-Badge (nur strassenpulver bleibt darunter).
 */
export function getLaborOutputBadge(itemKey: string): ItemBadge | null {
  const m = /^output_([a-z0-9_]+)__([a-z_]+)$/.exec(itemKey);
  if (m) {
    const [, product, pulverSorte] = m;
    return getLabPurityBadge(pulverSorte ?? '', product) ?? getPulverBadge(pulverSorte ?? '');
  }
  return getLabPurityBadge(itemKey) ?? getPulverBadge(itemKey); // Pulver-Linie direkt (strassenpulver etc.)
}

/** Quality- + Variant-Badge (sofern vorhanden) als zusammenhängendes HTML-Fragment, leerer String falls keines zutrifft. */
export function renderItemBadges(itemKey: string, cssClass: string): string {
  const quality  = getQualityBadge(itemKey);
  const variant  = getVariantBadge(itemKey);
  const labOutput = getLaborOutputBadge(itemKey);
  return [quality, variant, labOutput].filter((b): b is ItemBadge => b !== null).map((b) => badgeHtml(b, cssClass)).join(' ');
}
